import { createServerFn } from "@tanstack/react-start";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";

export const getAdminOverview = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    const { assertAdmin } = await import("./admin.server");
    await assertAdmin(context.userId);
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");

    const [vendors, products, orders, subs] = await Promise.all([
      supabaseAdmin.from("vendors").select("id, store_name, slug, city, status, created_at, profile_id, profiles:profile_id(email, auth_user_id)").order("created_at", { ascending: false }).limit(2000),
      supabaseAdmin.from("products").select("id, title, slug, price, status, in_stock, vendor_id, created_at").order("created_at", { ascending: false }).limit(5000),
      supabaseAdmin.from("orders").select("id, customer_email, amount_total, currency, status, created_at").order("created_at", { ascending: false }).limit(5000),
      supabaseAdmin.from("subscriptions").select("id, customer_email, product_name, amount, currency, status, current_period_end, profile_id").order("created_at", { ascending: false }).limit(2000),
    ]);

    // Count published products per vendor and fetch last_sign_in_at from auth.users
    const vendorIds = (vendors.data ?? []).map((v) => v.id);
    const authUserIds = (vendors.data ?? [])
      .map((v) => (v as { profiles?: { auth_user_id?: string } | null }).profiles?.auth_user_id)
      .filter((x): x is string => !!x);

    const publishedCountByVendor = new Map<string, number>();
    if (vendorIds.length) {
      const { data: pubRows } = await supabaseAdmin
        .from("products")
        .select("vendor_id")
        .in("vendor_id", vendorIds)
        .eq("status", "publish");
      for (const r of pubRows ?? []) {
        const vid = (r as { vendor_id: string }).vendor_id;
        publishedCountByVendor.set(vid, (publishedCountByVendor.get(vid) ?? 0) + 1);
      }
    }

    const lastSignInByUser = new Map<string, string | null>();
    await Promise.all(
      authUserIds.map(async (uid) => {
        try {
          const { data } = await supabaseAdmin.auth.admin.getUserById(uid);
          lastSignInByUser.set(uid, data.user?.last_sign_in_at ?? null);
        } catch {
          lastSignInByUser.set(uid, null);
        }
      }),
    );

    const vendorsEnriched = (vendors.data ?? []).map((v) => {
      const prof = (v as { profiles?: { email?: string | null; auth_user_id?: string | null } | null }).profiles;
      const authId = prof?.auth_user_id ?? null;
      return {
        ...v,
        email: prof?.email ?? null,
        last_sign_in_at: authId ? lastSignInByUser.get(authId) ?? null : null,
        published_count: publishedCountByVendor.get(v.id) ?? 0,
      };
    });

    const { count: vendorCount } = await supabaseAdmin.from("vendors").select("*", { count: "exact", head: true });
    const { count: productCount } = await supabaseAdmin.from("products").select("*", { count: "exact", head: true });
    const { count: orderCount } = await supabaseAdmin.from("orders").select("*", { count: "exact", head: true });

    const revenue = (orders.data ?? [])
      .filter((o) => o.status === "paid")
      .reduce((s, o) => s + Number(o.amount_total ?? 0), 0);

    // Enrich subscriptions with last_sign_in_at of the owning profile
    const subProfileIds = Array.from(
      new Set(
        (subs.data ?? [])
          .map((s) => (s as { profile_id?: string | null }).profile_id)
          .filter((x): x is string => !!x),
      ),
    );
    const authUserByProfile = new Map<string, string | null>();
    if (subProfileIds.length) {
      const { data: profRows } = await supabaseAdmin
        .from("profiles")
        .select("id, auth_user_id")
        .in("id", subProfileIds);
      for (const p of profRows ?? []) {
        authUserByProfile.set(p.id as string, (p as { auth_user_id?: string | null }).auth_user_id ?? null);
      }
    }
    const missingAuthIds = Array.from(
      new Set(
        Array.from(authUserByProfile.values()).filter(
          (uid): uid is string => !!uid && !lastSignInByUser.has(uid),
        ),
      ),
    );
    await Promise.all(
      missingAuthIds.map(async (uid) => {
        try {
          const { data } = await supabaseAdmin.auth.admin.getUserById(uid);
          lastSignInByUser.set(uid, data.user?.last_sign_in_at ?? null);
        } catch {
          lastSignInByUser.set(uid, null);
        }
      }),
    );
    const subsEnriched = (subs.data ?? []).map((s) => {
      const pid = (s as { profile_id?: string | null }).profile_id ?? null;
      const authId = pid ? authUserByProfile.get(pid) ?? null : null;
      return {
        ...s,
        last_sign_in_at: authId ? lastSignInByUser.get(authId) ?? null : null,
      };
    });

    return {
      stats: {
        vendors: vendorCount ?? 0,
        products: productCount ?? 0,
        orders: orderCount ?? 0,
        revenue,
      },
      vendors: vendorsEnriched,
      products: products.data ?? [],
      orders: orders.data ?? [],
      subscriptions: subsEnriched,
    };
  });


import { z } from "zod";

export const setVendorStatus = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input) => z.object({
    vendor_id: z.string().uuid(),
    status: z.enum(["active", "inactive", "pending"]),
  }).parse(input))
  .handler(async ({ data, context }) => {
    const { assertAdmin } = await import("./admin.server");
    await assertAdmin(context.userId);
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { error } = await supabaseAdmin
      .from("vendors")
      .update({ status: data.status })
      .eq("id", data.vendor_id);
    if (error) throw new Error(error.message);
    return { ok: true };
  });

export const sendVendorPasswordReset = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input) => z.object({ vendor_id: z.string().uuid() }).parse(input))
  .handler(async ({ data, context }) => {
    const { assertAdmin } = await import("./admin.server");
    await assertAdmin(context.userId);
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");

    const { data: vendor } = await supabaseAdmin
      .from("vendors")
      .select("id, profile_id, profiles:profile_id(email, auth_user_id)")
      .eq("id", data.vendor_id)
      .maybeSingle();

    const prof = (vendor as { profiles?: { email?: string | null; auth_user_id?: string | null } | null })?.profiles;
    const email = prof?.email;
    if (!email) throw new Error("Prodajalec nima e-naslova.");

    const SUPABASE_URL = process.env.SUPABASE_URL!;
    const SUPABASE_PUBLISHABLE_KEY = process.env.SUPABASE_PUBLISHABLE_KEY!;

    const res = await fetch(`${SUPABASE_URL}/auth/v1/recover`, {
      method: "POST",
      headers: {
        "apikey": SUPABASE_PUBLISHABLE_KEY,
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        email,
        gotrue_meta_security: {},
      }),
    });

    if (!res.ok) {
      const body = await res.text();
      throw new Error(`Napaka pri pošiljanju: ${body}`);
    }

    return { success: true, email };
  });

export const syncStripeSubscriptions = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    const { assertAdmin } = await import("./admin.server");
    await assertAdmin(context.userId);

    const secret = process.env.STRIPE_SECRET_KEY;
    if (!secret) throw new Error("Stripe ni konfiguriran.");

    const { default: Stripe } = await import("stripe");
    const { upsertStripeSubscription } = await import("./stripe-subscription-sync.server");
    const stripe = new Stripe(secret);

    let synced = 0;
    let checked = 0;
    let startingAfter: string | undefined;

    do {
      const page = await stripe.subscriptions.list({ limit: 100, status: "all", starting_after: startingAfter });
      for (const sub of page.data) {
        checked += 1;
        const result = await upsertStripeSubscription(stripe, sub);
        if (result.synced) synced += 1;
      }
      startingAfter = page.has_more ? page.data.at(-1)?.id : undefined;
    } while (startingAfter);

    return { checked, synced };
  });

export const checkIsAdmin = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { data: profile } = await supabaseAdmin
      .from("profiles").select("id").eq("auth_user_id", context.userId).maybeSingle();
    if (!profile) return { isAdmin: false };
    const { data: role } = await supabaseAdmin
      .from("user_roles").select("role").eq("profile_id", profile.id).eq("role", "admin").maybeSingle();
    return { isAdmin: !!role };
  });

export const getRandomVendorWithProducts = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    const { assertAdmin } = await import("./admin.server");
    await assertAdmin(context.userId);
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");

    const { data: vendors } = await supabaseAdmin
      .from("vendors")
      .select("id, store_name, slug, city, status")
      .eq("status", "active");

    const activeVendors = vendors ?? [];
    if (activeVendors.length === 0) return { vendor: null, products: [] };

    const randomVendor = activeVendors[Math.floor(Math.random() * activeVendors.length)];

    const { data: products } = await supabaseAdmin
      .from("products")
      .select("id, title, slug, price, featured_image_url, status")
      .eq("vendor_id", randomVendor.id)
      .eq("status", "publish")
      .order("published_at", { ascending: false })
      .limit(4);

  return { vendor: randomVendor, products: products ?? [] };
  });

export const getCronSecret = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    const { assertAdmin } = await import("./admin.server");
    await assertAdmin(context.userId);
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { data } = await supabaseAdmin
      .from("app_settings")
      .select("value")
      .eq("key", "cron_secret")
      .maybeSingle();
    const value = (data as { value?: string } | null)?.value || "";
    const masked = value.length > 8 ? `********${value.slice(-4)}` : (value ? "********" : "");
    return { masked, hasValue: !!value };
  });

export const updateCronSecret = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input) =>
    z
      .object({
        secret: z.string().min(1, "Geslo ne sme biti prazno.").max(256, "Geslo je predolgo."),
      })
      .parse(input)
  )
  .handler(async ({ data, context }) => {
    const { assertAdmin } = await import("./admin.server");
    await assertAdmin(context.userId);
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { error } = await supabaseAdmin
      .from("app_settings")
      .upsert({ key: "cron_secret", value: data.secret, updated_at: new Date().toISOString() });
    if (error) throw new Error(error.message);
    return { ok: true };
  });

export const getFacebookSettings = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    const { assertAdmin } = await import("./admin.server");
    await assertAdmin(context.userId);
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { data } = await supabaseAdmin
      .from("app_settings")
      .select("key, value")
      .in("key", ["fb_page_id", "fb_access_token", "fb_contact_email", "fb_token_expires_at", "fb_app_id", "fb_app_secret"]);
    const map = new Map((data ?? []).map((r) => [r.key as string, (r as { value?: string }).value ?? ""]));
    const token = map.get("fb_access_token") || process.env.FB_ACCESS_TOKEN || "";
    const appSecret = map.get("fb_app_secret") || process.env.FB_APP_SECRET || "";
    const expiresAt = map.get("fb_token_expires_at") ?? "";
    let daysUntilExpiry: number | null = null;
    if (expiresAt) {
      const exp = new Date(expiresAt);
      const now = new Date();
      const msPerDay = 24 * 60 * 60 * 1000;
      daysUntilExpiry = Math.round((exp.getTime() - now.getTime()) / msPerDay);
    }
    return {
      page_id: map.get("fb_page_id") ?? "",
      contact_email: map.get("fb_contact_email") ?? "",
      access_token_masked: token.length > 8 ? `********${token.slice(-4)}` : (token ? "********" : ""),
      has_access_token: !!token,
      token_expires_at: expiresAt,
      days_until_expiry: daysUntilExpiry,
      app_id: map.get("fb_app_id") ?? "",
      app_secret_masked: appSecret.length > 4 ? `********${appSecret.slice(-4)}` : (appSecret ? "********" : ""),
      has_app_secret: !!appSecret,
    };
  });


export const updateFacebookSettings = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input) =>
    z
      .object({
        page_id: z.string().trim().max(64).optional(),
        contact_email: z.string().trim().email("Neveljaven e-naslov.").max(255).optional().or(z.literal("")),
        access_token: z.string().trim().max(4096).optional(),
        token_expires_at: z.string().trim().max(32).optional().or(z.literal("")),
        app_id: z.string().trim().max(64).optional(),
        app_secret: z.string().trim().max(256).optional(),
      })
      .parse(input)
  )
  .handler(async ({ data, context }) => {
    const { assertAdmin } = await import("./admin.server");
    await assertAdmin(context.userId);
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");

    const rows: { key: string; value: string; updated_at: string }[] = [];
    const now = new Date().toISOString();
    let check: Awaited<ReturnType<typeof import("./facebook.server").inspectFbToken>> | null = null;

    if (data.page_id !== undefined) rows.push({ key: "fb_page_id", value: data.page_id, updated_at: now });
    if (data.contact_email !== undefined) rows.push({ key: "fb_contact_email", value: data.contact_email, updated_at: now });
    if (data.app_id !== undefined) rows.push({ key: "fb_app_id", value: data.app_id, updated_at: now });
    if (data.app_secret !== undefined && data.app_secret.length > 0) {
      rows.push({ key: "fb_app_secret", value: data.app_secret, updated_at: now });
    }

    // App ID/Secret shrani takoj, da ju lahko uporabimo za podaljšanje tokena.
    if (rows.length > 0) {
      const { error: preError } = await supabaseAdmin.from("app_settings").upsert(rows);
      if (preError) throw new Error(preError.message);
    }

    const tokenRows: { key: string; value: string; updated_at: string }[] = [];
    let exchanged = false;
    if (data.access_token !== undefined && data.access_token.length > 0) {
      const { inspectFbToken, exchangeForLongLivedToken } = await import("./facebook.server");
      const ex = await exchangeForLongLivedToken(data.access_token);
      exchanged = ex.exchanged;
      check = await inspectFbToken(ex.token, (data.page_id ?? "").trim());
      if (!check.valid) {
        throw new Error(`Token ni shranjen — Facebook ga zavrača: ${check.message}`);
      }
      tokenRows.push({ key: "fb_access_token", value: ex.token, updated_at: now });
      if (check.expires_at) {
        tokenRows.push({ key: "fb_token_expires_at", value: check.expires_at.slice(0, 10), updated_at: now });
      } else if (check.never_expires) {
        tokenRows.push({ key: "fb_token_expires_at", value: "", updated_at: now });
      }
    }
    if (data.token_expires_at !== undefined && !tokenRows.some((r) => r.key === "fb_token_expires_at")) {
      tokenRows.push({ key: "fb_token_expires_at", value: data.token_expires_at, updated_at: now });
    }
    if (tokenRows.length > 0) {
      const { error } = await supabaseAdmin.from("app_settings").upsert(tokenRows);
      if (error) throw new Error(error.message);
    }
    return { ok: true, check, exchanged };
  });

export const refreshFacebookToken = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    const { assertAdmin } = await import("./admin.server");
    await assertAdmin(context.userId);
    const { refreshPageAccessToken } = await import("./facebook.server");
    const result = await refreshPageAccessToken();
    if (!result.ok) throw new Error(result.error || "Podaljšanje tokena ni uspelo.");
    return result;
  });


export const verifyFacebookToken = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    const { assertAdmin } = await import("./admin.server");
    await assertAdmin(context.userId);
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { data } = await supabaseAdmin
      .from("app_settings")
      .select("key, value")
      .in("key", ["fb_page_id", "fb_access_token"]);
    const map = new Map((data ?? []).map((r) => [r.key as string, (r as { value?: string }).value ?? ""]));
    const token = map.get("fb_access_token") || process.env.FB_ACCESS_TOKEN || "";
    if (!token) throw new Error("Access token ni nastavljen.");
    const { inspectFbToken } = await import("./facebook.server");
    return await inspectFbToken(token, map.get("fb_page_id") ?? "");
  });


export const getFacebookPostLog = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    const { assertAdmin } = await import("./admin.server");
    await assertAdmin(context.userId);
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { data, error } = await (supabaseAdmin.from("fb_publish_history" as never) as unknown as {
      select: (c: string) => {
        order: (col: string, o: { ascending: boolean }) => {
          limit: (n: number) => Promise<{ data: Array<Record<string, unknown>> | null; error: { message: string } | null }>;
        };
      };
    })
      .select("article_slug, article_url, fb_post_id, status, error_message, attempt_count, published_at")
      .order("published_at", { ascending: false })
      .limit(50);
    if (error) throw new Error(error.message);
    return (data ?? []).map((r) => ({
      article_slug: r.article_slug as string,
      article_url: (r.article_url as string) ?? null,
      fb_post_id: (r.fb_post_id as string) ?? null,
      status: (r.status as string) ?? "success",
      error_message: (r.error_message as string) ?? null,
      attempt_count: (r.attempt_count as number) ?? 1,
      posted_at: r.published_at as string,
    }));
  });

export const triggerFacebookPost = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    const { assertAdmin } = await import("./admin.server");
    await assertAdmin(context.userId);
    const { publishDailyToFacebook } = await import("./facebook.server");
    const result = await publishDailyToFacebook({ force: true });
    if (!result.ok) throw new Error(result.error || "FB objava ni uspela.");
    return {
      ok: true,
      skipped: result.skipped,
      slug: result.article?.slug,
      title: result.article?.title,
      fb_post_id: result.fb_post_id,
    };
  });


